import type { MonitoringData, MonitoringSendData, UserData, MonitorPlugin, LocationData } from "../types/type";
import { Span } from "./span";
import { generateRandomID } from "../utils/tool";


export type InitOptions = {
  endpoint: string;
  enable: boolean;
  app: string;
  version: string;
  deviceId?: string;
  platform?: string;
  refId?: string;
  bufferSize?: number;
  flushInterval?: number;
  debug?: boolean;
}


export default class MonitorSdk {
  private endpoint: string;
  private plugins: MonitorPlugin[] = [];
  private buffer: any[] = [];
  private bufferSize: number;
  private flushInterval: number;
  private flushTimer: ReturnType<typeof setInterval> | null = null;
  private spanId: string = '';
  public user: UserData = {};
  public location: LocationData = {};
  public createTime: number;
  public app: string;
  public version: string = '';
  public traceId: string = '';
  public deviceId: string | undefined;
  public platform: string | undefined;
  public enable: boolean = true;
  public debug: boolean = false;

  constructor({ enable, debug, app, endpoint, deviceId, version, platform, bufferSize = 10, flushInterval = 1000 }: InitOptions) {
    this.endpoint = endpoint;
    this.bufferSize = bufferSize; // 缓冲区大小
    this.flushInterval = flushInterval; // 自动刷新间隔（毫秒）
    // this.traceId = generateRandomID(32); // 生成一个随机的 traceId
    // this.spanId = generateRandomID(16); // 生成一个随机的 spanId
    this.createTime = Date.now(); // 创建时间
    this.app = app; // 应用名称
    this.deviceId = deviceId;
    this.platform = platform;
    this.version = version;
    this.enable = enable
    this.debug = debug || false;
    // 启动自动刷新计时器
    // this.startFlushTimer();
  }

  public setTraceId(id: string) {
    this.traceId = id;
  }

  public setSpanId(spanId: string) {
    this.spanId = spanId;
  }

  public getEndpoint() {
    return this.endpoint;
  }

  // 注册插件
  public use(plugin: MonitorPlugin) {
    plugin.init(this);
    this.plugins.push(plugin);
  }

  /**
   * 添加数据到缓冲区
   * @param data 上报的数据
   * @param samplingRate 采样率
   * @param immediate 是否立即发送
   */
  public report(data: MonitoringData, samplingRate = 1, immediate = false) {
    if (!data) return;
    if (!data.timestamp) data.timestamp = Date.now();

    if (this.shouldSample(samplingRate) === false) {
      console.log("Data not sent due to sampling.");
      return;
    }

    this.buffer.push(data);

    // 如果缓冲区达到指定大小，则立即发送
    if (immediate || this.buffer.length >= this.bufferSize) {
      this.flush();
    }
  }

  private shouldSample(samplingRate: number) {
    return Math.random() < samplingRate;
  }

  public setUser(data: UserData) {
    this.user = data
  }

  public setLocation(data: LocationData) {
    this.location = data
  }

  // 手动刷新缓冲区，将数据发送到服务器
  public flush() {
    if (this.buffer.length === 0) return;

    const baseInfo = this.plugins.reduce((s, plugin) => {
      return {
        ...s,
        ...plugin.addBaseInfo?.(),
      };
    }, {});

    const dataToSend = this.buffer.map((data) => {
      return {
        ...baseInfo,
        ...data,
        traceId: this.traceId,
        spanId: this.spanId,
        app: this.app,
        version: this.version,
        user: this.user,
        location: this.location,
        platform: this.platform,
        sdk: __VERSION__,
      };
    });

    this.buffer = [];

    if (this.enable) {
      this.send(dataToSend);
    }
    if (this.debug || !this.enable) {
      console.log('monitor dataToSend:', dataToSend)
    }
  }


  send(_dataToSend: MonitoringSendData[]) {
    console.error('The send method is not implemented')
  }

  // 启动定时器以定期刷新缓冲区
  public startFlushTimer() {
    this.flushTimer = setInterval(() => this.flush(), this.flushInterval);
  }

  // 停止定时器
  public stopFlushTimer() {
    if (this.flushTimer) {
      clearInterval(this.flushTimer);
      this.flushTimer = null;
    }
  }

  // 创建一个新的 span
  public createSpan(
    action: string,
    traceId: string,
    parentSpanId?: string
  ): Span {
    const spanId = generateRandomID(16);
    if (!parentSpanId) parentSpanId = this.spanId;
    const span = new Span({
      traceId,
      spanId,
      action,
      parentSpanId,
      sdk: this,
    });
    return span;
  }
}
